import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  availableLanguages: Array<{
    code: string;
    name: string;
    nativeName: string;
  }>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string>('pt');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const availableLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
    { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
    { code: 'de', name: 'German', nativeName: 'Deutsch' },
    { code: 'zh', name: 'Chinese', nativeName: '中文' },
    { code: 'ko', name: 'Korean', nativeName: '한국어' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語' }
  ];

  // Normalize a possibly-regional code (e.g. `pt-BR`) to the base language
  // code (`pt`) we expose in `availableLanguages`, so the selector highlights
  // the right entry and persistence is consistent.
  const baseCode = (lng?: string): string => (lng || 'pt').split('-')[0].toLowerCase();

  useEffect(() => {
    // Set initial language from i18n
    setCurrentLanguage(baseCode(i18n.resolvedLanguage || i18n.language));
    setIsLoading(false);

    // Listen for language changes
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(baseCode(lng));
    };
    // In tests, i18n might be a mocked object without event API
    const hasEventApi = typeof (i18n as any).on === 'function' && typeof (i18n as any).off === 'function';
    if (hasEventApi) {
      (i18n as any).on('languageChanged', handleLanguageChanged);
    }

    return () => {
      if (hasEventApi) {
        (i18n as any).off('languageChanged', handleLanguageChanged);
      }
    };
  }, [i18n]);

  const setLanguage = (language: string) => {
    if (availableLanguages.some(lang => lang.code === language)) {
      i18n.changeLanguage(language);
      localStorage.setItem('nice-agenda-language', language);
    }
  };

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    availableLanguages,
    isLoading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
